import * as React from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Row } from '@tekvoice/shared';
import { useAudioPlayer } from '@/components/audio-player';
import { REVIEW_GRID, ReviewRow } from '@/components/review/review-row';
import { Checkbox } from '@/components/ui/checkbox';
import { assetAudio } from '@/lib/audio';
import { cn } from '@/lib/utils';

/** SPEC §11 — virtualize above 100 rows. */
export const VIRTUALIZE_THRESHOLD = 100;
const ESTIMATED_ROW_HEIGHT = 96;

interface ReviewTableProps {
  rows: Row[];
  selectedIds: ReadonlySet<string>;
  onToggleRow: (rowId: string, selected: boolean) => void;
  onToggleAll: (selected: boolean) => void;
  emptyMessage: string;
}

export function ReviewTable({
  rows,
  selectedIds,
  onToggleRow,
  onToggleAll,
  emptyMessage,
}: ReviewTableProps): React.JSX.Element {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const player = useAudioPlayer();
  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const virtualize = rows.length > VIRTUALIZE_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 10,
    enabled: virtualize,
    // jsdom (and the very first paint) reports a 0×0 scroller; without a starting
    // rect the window would be empty until a resize that never comes.
    initialRect: { width: 1280, height: 800 },
  });

  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  const someSelected = rows.some((row) => selectedIds.has(row.id));

  /** Nice-to-have from SPEC §11: j/k to move, space to play the English clip. */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }
    if (event.key === 'j' || event.key === 'k') {
      event.preventDefault();
      setFocusedIndex((current) => {
        const next = Math.min(rows.length - 1, Math.max(0, current + (event.key === 'j' ? 1 : -1)));
        if (virtualize) virtualizer.scrollToIndex(next, { align: 'auto' });
        return next;
      });
      return;
    }
    if (event.key === ' ') {
      const row = rows[focusedIndex];
      if (!row) return;
      const audio = assetAudio(row.assets.EN);
      if (!audio) return;
      event.preventDefault();
      player.toggle(`${row.assets.EN?.id}:${audio.source}:${row.assets.EN?.currentTakeId}`, audio.url);
    }
  };

  const renderRow = (row: Row, index: number): React.JSX.Element => (
    <ReviewRow
      row={row}
      selected={selectedIds.has(row.id)}
      focused={index === focusedIndex}
      onSelectedChange={(selected) => onToggleRow(row.id, selected)}
    />
  );

  return (
    <div
      role="table"
      aria-label="Review rows"
      aria-rowcount={rows.length}
      className="rounded-lg border"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        role="row"
        className={cn(
          REVIEW_GRID,
          'sticky top-0 z-10 items-center border-b bg-muted/50 py-2 text-xs font-medium text-muted-foreground',
        )}
      >
        <div role="columnheader">
          <Checkbox
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            aria-label="Select all rows"
            onCheckedChange={(value) => onToggleAll(value === true)}
          />
        </div>
        <div role="columnheader">#</div>
        <div role="columnheader">English</div>
        <div role="columnheader">Arabic</div>
        <div role="columnheader">EN audio</div>
        <div role="columnheader">AR audio</div>
        <div role="columnheader">Actions</div>
      </div>

      {rows.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
      ) : virtualize ? (
        <div ref={scrollRef} className="max-h-[70vh] overflow-auto" data-testid="virtual-scroller">
          <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
            {virtualizer.getVirtualItems().map((item) => (
              <div
                key={rows[item.index].id}
                data-index={item.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${item.start}px)`,
                }}
              >
                {renderRow(rows[item.index], item.index)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          {rows.map((row, index) => (
            <React.Fragment key={row.id}>{renderRow(row, index)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
